import { getRecordFormat, type ThoughtRecord } from '@adhd/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { RecordSheet } from '../components/RecordSheet.js';
import { ROWS_PER_SHEET, WorksheetSheet } from '../components/WorksheetSheet.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';

/**
 * Your thought records as a document, to print or hand over.
 *
 * The document is the page. There is no second layout that appears only at
 * print time — the controls are marked .no-print and everything under them is
 * exactly what comes out of the printer. So changing the range shows you the
 * real thing you are about to give someone, not a preview of it.
 *
 * Records print oldest first, which is the opposite of the log. The log is
 * newest-first because you are looking for the thing you just wrote; a document
 * is read start to finish by someone following what changed over the weeks, and
 * belief ratings only tell that story in the order they happened.
 *
 * Only your own records come out of here. Blank worksheets to fill in by hand
 * are their own screen, and keeping them there is what makes anything printed
 * from this one reliably a single thing rather than half records and half empty
 * paper. The way to keep that true is for this screen not to know blanks exist.
 *
 * The table layout prints sideways, which is a property of the sheet of paper
 * rather than of any element on it, so it is switched with a class on <body>
 * that the print stylesheet reads. Anything that writes to a shared element like
 * that owes a removal on the way out: left behind, it would silently rotate
 * every print made from any other screen for the rest of the session.
 *
 * `?record=<id>` prints one record on its own, which is how the record's own
 * screen links here. The intent arriving that way is "this one", so the range
 * controls are gone — left up, they are a way to print your whole log by
 * accident while thinking you are printing a single sheet.
 */

/** The server's per-page ceiling. Asking for more is a validation error. */
const PAGE_SIZE = 200;

/**
 * A stop against following cursors forever. Ten pages is far more than anyone
 * will have written, so in practice this never bites — and when it does the
 * document says so rather than trimming in silence.
 */
const MAX_PAGES = 10;

/**
 * The hook the print stylesheet hangs its landscape @page off.
 *
 * @page cannot be selected into from an element, so the orientation of the
 * paper cannot be set by the table component itself — something above the page
 * has to say which way round this print is. Kept as a constant because the
 * add and the remove are in different places and a typo in one of them fails
 * silently: nothing throws, the print simply comes out the wrong way round.
 */
const LANDSCAPE_CLASS = 'print-landscape';

type Layout = 'sheets' | 'table';

/**
 * What each layout actually puts on the paper.
 *
 * Written out at length because nobody choosing this for the first time knows
 * which one their therapist wants, and the difference is not guessable from two
 * words. Neither is described as better: which one is right depends entirely on
 * whether the person reading wants to follow one record closely or scan several.
 */
const LAYOUTS: { key: Layout; title: string; what: string }[] = [
  {
    key: 'sheets',
    title: 'One worksheet per record',
    what: 'Each record laid out like the paper worksheets: what happened, the thought, the evidence both ways, and what you landed on. Everything you wrote, in full. Short ones share a sheet of paper.',
  },
  {
    key: 'table',
    title: 'The worksheet grid',
    what: 'The sideways layout of the printed thought record — one situation to a sheet, with the evidence for and against stacked in the middle. What a therapist is used to reading.',
  },
];

interface Fetched {
  records: ThoughtRecord[];
  /** False when the range held more records than MAX_PAGES would fetch. */
  whole: boolean;
}

/**
 * The whole range, not the first page of it.
 *
 * An export that quietly stopped at the page limit would hand a therapist a
 * document that looks complete and is not, which is the one failure here that
 * nobody in the room can see. So this follows the cursor to the end.
 */
async function fetchRange(since: string | null): Promise<Fetched> {
  const records: ThoughtRecord[] = [];
  let cursor: string | null = null;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
    if (since) params.set('from', startOfLocalDay(since));
    if (cursor) params.set('cursor', cursor);

    const batch = await api.listThoughtRecords(`?${params.toString()}`);
    records.push(...batch.items);
    if (!batch.nextCursor) return { records, whole: true };
    cursor = batch.nextCursor;
  }

  return { records, whole: false };
}

export function Export(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [since, setSince] = useState<string | null>(() => daysAgo(28));
  const [includeUnfinished, setIncludeUnfinished] = useState(true);
  const [layout, setLayout] = useState<Layout>('sheets');
  const [fetched, setFetched] = useState<Fetched | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const recordId = searchParams.get('record');
  const single = recordId !== null;
  // Controls that are not on screen must stop acting. A 'table' choice left over
  // from before would otherwise print a single record sideways with nothing
  // visible to explain why, and no way to change it back.
  const tableLayout = layout === 'table' && !single;

  const today = localDay(new Date());
  // Fixed at mount so the pressed chip does not quietly let go of itself if the
  // page is left open across midnight.
  const presets = useMemo(
    () => [
      { label: 'Last 4 weeks', since: daysAgo(28) },
      { label: 'Last 3 months', since: monthsAgo(3) },
      { label: 'Everything', since: null },
    ],
    [],
  );

  useEffect(() => {
    // Only the range is fetched from the server. The unfinished switch filters
    // what is already here and the layout choice only re-arranges it, so neither
    // of them costs a request.
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Dropped rather than left on screen under the spinner. Otherwise the count
    // in the heading and the print button both describe the range you just
    // navigated away from, and printing mid-load gives a title with no records
    // under it.
    setFetched(null);
    // One record asked for by id is fetched by id. Finding it by hunting through
    // a range would fail exactly when it matters: a record older than whatever
    // range happened to be selected simply would not be in the response.
    const load: Promise<Fetched> = recordId
      ? api.getThoughtRecord(recordId).then((record) => ({ records: [record], whole: true }))
      : fetchRange(since);
    load
      .then((result) => {
        if (!cancelled) setFetched(result);
      })
      .catch((e: unknown) => {
        // Landing on this screen offline is the likely case, and "Can't reach
        // the server" is already the right sentence for it.
        if (!cancelled) {
          setError(
            e instanceof ApiError
              ? e.message
              : recordId
                ? 'Could not load that record.'
                : 'Could not load your records.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Without this, tapping through the presets quickly can leave the slowest
    // response on screen under the range you did not choose.
    return () => {
      cancelled = true;
    };
  }, [since, recordId]);

  useEffect(() => {
    if (!tableLayout) return;
    document.body.classList.add(LANDSCAPE_CLASS);
    // The cleanup is the whole point of doing this in an effect rather than in
    // the click handler. It runs when the layout changes back and when this
    // screen unmounts — and <body> outlives both, so a class dropped on it here
    // would otherwise still be there, turning the next print from the log or a
    // journal entry sideways for no visible reason.
    return () => document.body.classList.remove(LANDSCAPE_CLASS);
  }, [tableLayout]);

  const all = fetched?.records ?? [];
  // The server offers `incompleteOnly`, which is the opposite of this switch —
  // there is no finished-only filter to ask for, so that half is done here. Not
  // applied to a single record: the switch is not on screen in that mode, and a
  // stale `false` would answer "print this one" with an empty page.
  const shown = single || includeUnfinished ? all : all.filter((r) => r.isComplete);
  const ordered = [...shown].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

  // Grouped by format first, because the five worksheets do not share a column
  // set — 'rejection' has no evidence-for step and 'catastrophe' asks different
  // questions — then chunked by ROWS_PER_SHEET, which the sheet component owns.
  // Encounter order, so the first sheet is the format most of the range is in
  // rather than whichever key happens to sort first.
  const worksheetSheets = (() => {
    const byFormat: { format: ReturnType<typeof getRecordFormat>; rows: ThoughtRecord[] }[] = [];
    for (const record of ordered) {
      const format = getRecordFormat(record.format);
      const group = byFormat.find((g) => g.format.key === format.key);
      if (group) group.rows.push(record);
      else byFormat.push({ format, rows: [record] });
    }
    return byFormat.flatMap((g) =>
      Array.from({ length: Math.ceil(g.rows.length / ROWS_PER_SHEET) }, (_, i) => ({
        format: g.format,
        rows: g.rows.slice(i * ROWS_PER_SHEET, (i + 1) * ROWS_PER_SHEET),
      })),
    );
  })();
  const unfinished = shown.filter((r) => !r.isComplete).length;
  const hiddenByToggle = all.length - shown.length;
  // Where the document really begins. When the range was truncated this is
  // later than the date the user picked, and the header has to say so rather
  // than asserting a range the paper does not cover.
  const coverageStart = ordered[0]?.occurredAt.slice(0, 10) ?? null;
  const truncated = fetched !== null && !fetched.whole;

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() => navigate(single ? `/thought-record/${recordId}` : '/days')}
        >
          <ArrowLeftIcon /> {single ? 'Back to the record' : 'Back to log'}
        </button>
        {/* The only h1 on the page, and it belongs to the document rather than
            to the screen: on paper this is the title, and a separate app-facing
            heading above it would print as a second one or leave the sheets
            hanging under nothing. */}
        <h1>
          Thought record{single ? '' : 's'}
          {user ? ` for ${user.displayName}` : ''}
        </h1>
        <p className="sheet-meta">
          {/* Suppressed for a single record, which has no range. "Since 17 July"
              printed above one sheet claims the weeks either side of it were
              empty. */}
          {!single && (
            <span>
              {truncated && coverageStart
                ? `From ${formatDay(coverageStart)}`
                : since
                  ? `Since ${formatDay(since)}`
                  : 'Everything to date'}
            </span>
          )}
          <span>Made on {formatDay(today)}</span>
          {/* No guard. Hidden when the count was zero, so an empty range printed
              a title, two dates and eight hundred pixels of white — a page that
              looks like a printer fault rather than an answer. The empty-state
              card that explains it is `.no-print`, correctly, because it holds
              a link; the count is the part that belongs on the paper. */}
          <span>
            {ordered.length === 0
              ? 'No records in this range'
              : `${ordered.length} ${ordered.length === 1 ? 'record' : 'records'}${
                  unfinished > 0 ? `, ${unfinished} unfinished` : ''
                }`}
          </span>
          {/* Outside the .no-print block on purpose. The switch itself vanishes
              from the printed page, so without this the paper reads as everything
              in the range while quietly omitting exactly the records this app
              tells you a therapist most wants to see. */}
          {hiddenByToggle > 0 && (
            <span>
              {hiddenByToggle} unfinished {hiddenByToggle === 1 ? 'record' : 'records'} left out
            </span>
          )}
        </p>
      </header>

      <div className="no-print stack">
        {/* One paragraph, not two. The first of the pair only said that this
            screen lays your records out as a document, which the heading above
            it already says, and two paragraphs of preamble ahead of the first
            choice is how three decisions start reading as a settings panel. The
            half worth keeping is where the paper ends up afterwards, because
            nothing else on the screen mentions it. */}
        <p className="muted">
          Everything you wrote, in your own words, with nothing summarised or left out. That makes
          it a copy of your mental health record living outside the app, and it goes wherever you
          leave it — worth a thought before you send it to a printer at work.
        </p>

        {/* The way back out of single-record mode. Without it the only route to
            the rest of your records is the browser's back button, and someone
            who arrived here from a link has no reason to think the whole log is
            one tap away. */}
        {single && (
          <div className="card card-quiet row-between">
            <span>This is one record on its own, not your whole log.</span>
            <button className="btn btn-secondary btn-compact" onClick={() => setSearchParams({})}>
              All your records
            </button>
          </div>
        )}

        {!single && (
          <>
            <div className="stack-sm stack">
              <div className="chips" role="group" aria-label="How far back to go">
                {presets.map((p) => (
                  <button
                    key={p.label}
                    className="chip"
                    aria-pressed={since === p.since}
                    onClick={() => setSince(p.since)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="field">
                <label htmlFor="since">Or start from a date</label>
                <input
                  id="since"
                  className="input"
                  type="date"
                  value={since ?? ''}
                  max={today}
                  onChange={(e) => setSince(e.target.value || null)}
                />
                <p className="hint">Leave it empty to include everything you've written.</p>
              </div>
            </div>

            <label className="option option-row">
              <input
                type="checkbox"
                checked={includeUnfinished}
                onChange={(e) => setIncludeUnfinished(e.target.checked)}
                style={{ accentColor: 'var(--accent)' }}
              />
              {/* A div rather than a span: .option span is the muted description
                  style, and nesting the title inside one greys the title too. */}
              <div className="grow" style={{ display: 'grid' }}>
                <strong>Include unfinished records</strong>
                <span>
                  The ones where you wrote the thought down and never got to the last step. Those
                  are often the ones a therapist most wants to see.
                </span>
              </div>
            </label>

            <div className="stack-sm stack">
              <p className="muted">
                Therapists differ on which of these they'd rather be handed. If you don't know, the
                worksheets are the usual thing to bring, and you can ask before the next one.
              </p>
              <div
                className="stack-sm stack"
                role="radiogroup"
                aria-label="How it comes out on paper"
              >
                {LAYOUTS.map((l) => (
                  <label className="option option-row" key={l.key}>
                    <input
                      type="radio"
                      name="export-layout"
                      checked={layout === l.key}
                      onChange={() => setLayout(l.key)}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    <div className="grow" style={{ display: 'grid' }}>
                      <strong>{l.title}</strong>
                      <span>{l.what}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Blank worksheets used to print from this screen, so anyone who used
            them that way comes back here first and finds the checkbox gone. With
            nowhere to send them, a feature that moved and a feature that was
            deleted look exactly the same.

            An underlined anchor in the accent colour rather than a quiet button:
            text that only turns out to be tappable once you touch it has hidden
            things from this app's own owner three times already. */}
        <p className="faint">
          Looking for blank worksheets to fill in by hand? Those live on{' '}
          <Link to="/worksheets" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
            the worksheets page
          </Link>
          .
        </p>
      </div>

      {loading ? (
        <div
          className="no-print"
          style={{ display: 'grid', placeItems: 'center', minHeight: '30dvh' }}
        >
          <div className="spinner" aria-label="Loading" />
        </div>
      ) : error ? (
        <div className="banner banner-error no-print">{error}</div>
      ) : ordered.length === 0 ? (
        <div className="card empty no-print">
          {hiddenByToggle > 0 ? (
            <p>
              Everything in this range is unfinished, and those are switched off above. Turn them
              back on, or pick a wider range.
            </p>
          ) : (
            <p>
              {/* "Try going further back" is a dead end for the person most
                  likely to be reading it: somebody who has not written a record
                  at all has nothing further back than their account, and this
                  is the screen whose whole job is handing something to a
                  therapist. The fetch is range-bound so it cannot tell "none
                  here" from "none ever" without another request — so the
                  sentence offers the action that works in both cases. */}
              Nothing to print in this range yet. Pick a wider range, or{' '}
              <Link className="linkish" to="/thought-record">
                work through a thought
              </Link>{' '}
              first.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Printed rather than hidden with the controls: paper that stops
              early is indistinguishable from paper that is complete, and the
              person reading it has no way to tell. */}
          {/* The advice here used to be "start from a later date to see the
              rest", which cannot work: the server returns the range newest
              first, so the records that fall off the end are the OLDEST, and a
              later start date returns a strict subset of what is already on the
              page. Following it would never have surfaced a single missing
              record. Say which end is missing, and where the document actually
              begins. */}
          {truncated && (
            <div className="banner banner-caution">
              This is the most recent {PAGE_SIZE * MAX_PAGES} records in the range, so the document
              starts on {formatDay(coverageStart ?? today)} rather than at the date chosen — the
              earlier ones are not here. To read further back, print this, then set the start date
              earlier and print again.
            </div>
          )}
          {tableLayout ? (
            // Grouped by format, then three situations to a sheet, the way the
            // paper one works: row 2 of the response answers row 2 of the
            // situation, and the number is the only thing that says so.
            worksheetSheets.map(({ format, rows }, i) => (
              <div className="ws-scroll" key={`${format.key}-${i}`}>
                <WorksheetSheet
                  records={rows}
                  format={format}
                  sheetNumber={i + 1}
                  sheetCount={worksheetSheets.length}
                />
              </div>
            ))
          ) : (
            <div className="doc-stage">{ordered.map((record) => <RecordSheet key={record.id} record={record} />)}</div>
          )}
        </>
      )}

      {/* Last child on purpose, and the placement is the whole trick. Sitting in
          the normal flow below the controls, where this started, put it 759px
          down an 812px viewport — under the fixed nav, which is --nav-height
          tall and won the hit test: elementFromPoint at the button's centre
          returned a nav link, so tapping where it appeared navigated away
          instead. Sticky alone did not fix that, because `position: sticky` with
          `bottom` pins an element only while there is still content BELOW it to
          scroll through, and above the records its natural position simply
          travelled up and off the screen at the first scroll. At the end of the
          container it stays pinned above the nav for the whole length of the
          document and releases at the bottom. */}
      {ordered.length > 0 && (
        <div className="print-action no-print">
          <button className="btn btn-primary btn-block" onClick={() => window.print()}>
            Print or save as PDF
          </button>
          {/* Said once, and quietly. There is no separate download here, and
              people who do not know the print dialogue can save a file go
              looking for a button that does not exist. */}
          <p className="faint" style={{ textAlign: 'center', marginTop: 'var(--space-2)' }}>
            For a file rather than paper, choose Save as PDF as the destination in the print
            dialogue.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * `YYYY-MM-DD` in the browser's own timezone.
 *
 * Not `toISOString().slice(0, 10)`, which gives the UTC day — that is tomorrow
 * for anyone east of Greenwich late in the evening, so "last 4 weeks" would
 * silently start and end on the wrong day.
 */
function localDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return localDay(date);
}

function monthsAgo(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return localDay(date);
}

/**
 * The instant local midnight began on that day.
 *
 * The server's `from` is an instant compared against `occurred_at` in UTC, not
 * a calendar day. A bare `YYYY-MM-DD` fails its validation outright, and UTC
 * midnight would slice hours off the edge of the range in either direction
 * depending on the offset — quietly dropping the first evening of it.
 */
function startOfLocalDay(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString();
}

function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  // The year is spelled out because this is read weeks later, on paper, in a
  // folder alongside other people's.
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
